/**
 * useOptimizationSubmit - Hook for handling optimization submission
 *
 * Encapsulates the complex logic for validating inputs, submitting optimizations,
 * and handling callbacks. Extracted from ScenarioEditor to improve maintainability.
 *
 * @module hooks/useOptimizationSubmit
 */

import { useCallback } from 'react';
import { validateTariffSelection, validateElectricTariff } from '../utils/tariffValidation';
import { parseBackendError } from '../utils/errorParser';
import { debug } from '../utils/debug';
import { API_BASE_URL } from '../services/api';
import { useAuth } from '../auth/useAuth';

/**
 * Compute a hash of current inputs for duplicate detection
 */
function computeInputsHash(scenarioForm, loadProfile) {
    return JSON.stringify({
        zipCode: scenarioForm.zipCode,
        loadProfileLength: loadProfile.loads?.length || 0,
        loadProfileSum: loadProfile.loads?.reduce((a, b) => a + b, 0) || 0,
        pvKw: scenarioForm.pvMaxKw,
        storageKw: scenarioForm.useStorage ? scenarioForm.storageMaxKw : 0,
        storageKwh: scenarioForm.useStorage ? scenarioForm.storageMaxKwh : 0,
        tariff: scenarioForm.customTariffData?.id || scenarioForm.blendedRate || 'none',
        offGrid: scenarioForm.offGrid,
    });
}

/**
 * Hook for handling optimization submission
 *
 * @param {Object} options
 * @param {Object} options.scenarioForm - Form state object with validation methods
 * @param {Object} options.loadProfile - Load profile state
 * @param {Array} options.missingFields - Array of missing required fields
 * @param {Object} options.optimization - Optimization hook instance
 * @param {Function} options.buildScenarioPayload - Function to build scenario payload
 * @param {Function} options.saveTransaction - Function to save scenario
 * @param {string} options.optimizationEngine - Selected optimization engine
 * @param {Object} options.cachedBauResults - Cached BAU results
 * @param {Function} options.setCachedBauResults - Function to cache BAU results
 * @param {string} options.projectId - Current project ID
 * @param {string} options.scenarioId - Current scenario ID
 * @param {Function} options.getHeaders - Function to get auth headers
 * @param {Object} options.resultsSavedRef - Ref tracking if results are saved
 * @param {Object} options.optimisticSync - Optimistic sync state object
 * @param {Function} options.reoptTransformWithYear - Results transform function
 * @param {Function} options.setSubmitConfirmation - Set confirmation message
 * @param {Function} options.setLastRunTimestamp - Set last run timestamp
 * @returns {Object} Object with handleSubmit function
 */
export function useOptimizationSubmit({
    scenarioForm,
    loadProfile,
    missingFields,
    optimization,
    buildScenarioPayload,
    saveTransaction,
    optimizationEngine,
    cachedBauResults,
    setCachedBauResults,
    projectId,
    scenarioId,
    getHeaders,
    resultsSavedRef,
    optimisticSync,
    reoptTransformWithYear,
    setSubmitConfirmation,
    setLastRunTimestamp,
}) {
    const { userProfile } = useAuth();

    const handleSubmit = useCallback(async () => {
        // Validate coordinates first with specific actionable message
        // This prevents 0,0 coordinates from reaching backend where they'd be rejected with less clear error
        const coords = scenarioForm.tariffLocation;
        if (!coords?.lat || !coords?.lon || coords.lat === 0 || coords.lon === 0) {
            scenarioForm.setSnackbar({
                open: true,
                message: "Invalid location coordinates. Please enter a valid US zip code in the Site tab to set the location.",
                severity: "error",
            });
            scenarioForm.setTab(0);  // Switch to Site tab where zip code input lives
            debug.error('[VALIDATION] Invalid coordinates:', coords, '- user needs to enter valid zip code');
            return;
        }

        // Block submission if critical inputs are missing
        if (missingFields.length > 0) {
            scenarioForm.setSnackbar({
                open: true,
                message: `Cannot run optimization - missing required inputs: ${missingFields.map(f => f.label).join(', ')}`,
                severity: "error",
            });
            return;
        }

        if (!loadProfile.loads || loadProfile.loads.length !== 8760) {
            scenarioForm.setSnackbar({
                open: true,
                message: "Load profile must have exactly 8760 hourly values",
                severity: "error",
            });
            return;
        }

        // Enhanced tariff validation - check form state first
        const tariffCheck = validateTariffSelection(scenarioForm);
        if (!tariffCheck.valid) {
            scenarioForm.setSnackbar({
                open: true,
                message: tariffCheck.error,
                severity: "error",
            });
            scenarioForm.setTab(1); // Switch to Utility tab (index 1)
            return;
        }

        // Show warning if present (but allow submission)
        if (tariffCheck.warning) {
            scenarioForm.setSnackbar({
                open: true,
                message: tariffCheck.warning,
                severity: "warning",
            });
        }

        // Validate Tariff - build payload and validate the actual ElectricTariff object
        const payloadCheck = buildScenarioPayload();

        // Validate the actual ElectricTariff object that will be sent to backend
        const tariffValidation = validateElectricTariff(
            payloadCheck.ElectricTariff,
            scenarioForm.offGrid
        );

        if (!tariffValidation.valid) {
            scenarioForm.setSnackbar({
                open: true,
                message: tariffValidation.error,
                severity: "error",
            });
            scenarioForm.setTab(1); // Switch to Utility tab (index 1)
            debug.error('[VALIDATION] Tariff validation failed:', tariffValidation.error);
            debug.error('[VALIDATION] ElectricTariff object:', payloadCheck.ElectricTariff);
            return;
        }

        // Compute current inputs hash to check for changes
        const currentInputsHash = computeInputsHash(scenarioForm, loadProfile);

        // Check if we already have results and inputs haven't changed
        // Compare current hash to the hash from the last completed run
        // Super admins and debug mode can bypass this check
        const allowDebugRerun = localStorage.getItem('allowDebugRerun') === 'true';
        const isSuperAdmin = userProfile?.is_super_admin || false;
        if (optimization.results && optimization.lastRunInputs === currentInputsHash && !allowDebugRerun && !isSuperAdmin) {
            scenarioForm.setSnackbar({
                open: true,
                message: "No changes detected since last run. Modify inputs to run a new optimization.",
                severity: "info",
            });
            return;
        }

        // Auto-save before running optimization (through saveTransaction to prevent race conditions)
        await saveTransaction();

        const scenario = buildScenarioPayload();

        // COMPREHENSIVE PAYLOAD LOGGING FOR DEBUGGING NPV DISCREPANCY
        debug.log('🔍 [MAIN SCENARIO] Complete payload being sent to solver:', {
            hasElectricTariff: !!scenario.ElectricTariff,
            hasElectricLoad: !!scenario.ElectricLoad,
            hasFinancial: !!scenario.Financial,
            hasGenerator: !!scenario.Generator,
            hasPV: !!scenario.PV,
            hasElectricStorage: !!scenario.ElectricStorage,
            loadProfileLength: scenario.ElectricLoad?.loads_kw?.length,

            // Complete Financial object
            Financial: scenario.Financial,

            // Complete Generator object
            Generator: scenario.Generator,

            // ElectricTariff (excluding long arrays)
            ElectricTariff: scenario.ElectricTariff ? {
                blended_annual_rates_us_dollars_per_kwh: scenario.ElectricTariff.blended_annual_rates_us_dollars_per_kwh,
                energy_rates_length: scenario.ElectricTariff.energy_rates?.length,
                demand_rates_length: scenario.ElectricTariff.demand_rates?.length
            } : null,

            // ElectricLoad (excluding the 8760 array)
            ElectricLoad: {
                year: scenario.ElectricLoad?.year,
                critical_load_fraction: scenario.ElectricLoad?.critical_load_fraction,
                loads_kw_length: scenario.ElectricLoad?.loads_kw?.length,
                loads_kw_sum: scenario.ElectricLoad?.loads_kw?.reduce((a, b) => a + b, 0)
            },

            // PV config
            PV: scenario.PV,

            // ElectricStorage config
            ElectricStorage: scenario.ElectricStorage,

            // Site
            Site: scenario.Site
        });

        // Log payload summary (without verbose load data)
        debug.log(`Submitting scenario using ${optimizationEngine} - loads: ${scenario?.ElectricLoad?.loads_kw?.length || 0} points`);

        optimization.submitScenario(scenario, {
            frontendSource: 'react-frontend-refactored',
            optimizationEngine: optimizationEngine,  // Pass selected solver
            cachedBauResults: cachedBauResults,  // Pass cached BAU to skip BAU model run
            transformResults: reoptTransformWithYear,
            onAlreadyRunning: () => {
                // Same scenario is already being optimized - inform user
                scenarioForm.setSnackbar({
                    open: true,
                    message: "This optimization is already running. Please wait for it to complete.",
                    severity: "info",
                });
            },
            onSuccess: async ({ runUuid }) => {
                // Save the run_uuid immediately so we can resume if user navigates away
                // IMPORTANT: Include full scenario data to avoid wiping ui_state
                debug.log('📌 Saving run_uuid immediately after submit:', runUuid);

                // Update local state FIRST so auto-save won't overwrite it
                optimization.setLastRunUuid(runUuid);
                optimization.setRunUuid(runUuid);

                try {
                    const headers = await getHeaders();
                    const uiState = {
                        form: scenarioForm.serializeFormState(),
                        loadProfile: loadProfile.serializeLoadProfile(),
                    };

                    await fetch(`${API_BASE_URL}/api/site_dna/v1/projects/${projectId}/scenarios/${scenarioId}`, {
                        method: 'PUT',
                        headers,
                        body: JSON.stringify({
                            name: scenarioForm.scenarioName || 'Untitled Scenario',
                            reopt_inputs: buildScenarioPayload(),
                            ui_state: uiState,
                            last_run_uuid: runUuid,
                            // Don't clear old results - keep them visible until new ones are ready
                        })
                    });
                    debug.log('✅ Run UUID saved to scenario with full ui_state');
                } catch (err) {
                    debug.error('Failed to save run_uuid:', err);
                }
            },
            onComplete: async (data) => {
                debug.log('🎉 onComplete callback fired! data.run_uuid:', data.run_uuid);

                // Cache BAU results for future runs
                if (optimization.bauResults) {
                    debug.log('📦 Caching BAU results for future runs');
                    setCachedBauResults(optimization.bauResults);
                }

                // Store inputs hash for duplicate detection on next run
                const completedInputsHash = computeInputsHash(scenarioForm, loadProfile);
                optimization.setLastRunInputs(completedInputsHash);

                scenarioForm.setSnackbar({
                    open: true,
                    message: `Optimization completed successfully (${optimizationEngine})!`,
                    severity: "success",
                });
                setSubmitConfirmation(`Run UUID: ${data.run_uuid}`);
                // Use actual completion timestamp from results if available
                const runTs = data.completed_at || data.timestamp;
                setLastRunTimestamp(runTs ? new Date(runTs) : new Date());

                // Auto-save results after optimization completes
                debug.log('Calling saveScenarioToBackend from onComplete with run_uuid:', data.run_uuid);

                // Reset results saved flag since we have new results to save
                resultsSavedRef.current = false;
                debug.log('🔄 [OPTIMIZATION] Resetting results saved flag for new optimization results');

                await saveTransaction(data.run_uuid, data);
            },
            onError: (err) => {
                const parsed = parseBackendError(err.message);

                // Show primary error message
                scenarioForm.setSnackbar({
                    open: true,
                    message: parsed.message,
                    severity: parsed.severity,
                });

                // If there's a suggested action, show it as a second notification
                if (parsed.action) {
                    setTimeout(() => {
                        scenarioForm.setSnackbar({
                            open: true,
                            message: `💡 ${parsed.action}`,
                            severity: "info",
                        });
                    }, 3500); // Show after first message auto-hides
                }
            },
        });
    }, [
        scenarioForm,
        loadProfile,
        missingFields,
        optimization,
        buildScenarioPayload,
        saveTransaction,
        optimizationEngine,
        cachedBauResults,
        setCachedBauResults,
        projectId,
        scenarioId,
        getHeaders,
        resultsSavedRef,
        reoptTransformWithYear,
        setSubmitConfirmation,
        setLastRunTimestamp,
        userProfile,
    ]);

    return { handleSubmit };
}

export default useOptimizationSubmit;
